import React from 'react';
import { Pencil } from 'lucide-react';
import { SLABadge } from '@/components/Service/SLABadge';
import { StatusPill } from '@/components/Service/StatusPill';
import { jobMetrics } from '@/components/Service/jobMetrics';

/**
 * Two-row report table used by every tab on `/service/reports`.
 *
 * Props:
 *   • rows           — already-filtered, already-paginated row array
 *   • selected       — Set of selected row ids (for bulk-edit)
 *   • togglePick     — (id) => void
 *   • onEdit         — (id) => void; navigates to /service/jobs/<id>/edit
 *   • startSno       — running S.No offset for the first row (page start)
 *   • showJobMetrics — when true, adds "Job Hours" + "Days Delay" primary
 *                      columns (used by Technician / Job Status / Service Head
 *                      / Job Type / Supervisor reports). Vehicle Inwards /
 *                      Outwards leave this off.
 */
export function ReportTable({ rows, selected, togglePick, onEdit, startSno = 0, showJobMetrics = false }) {
  const minWidth = showJobMetrics ? 1700 : 1500;
  return (
    <div className="overflow-auto max-h-[70vh] relative">
      <table className="w-full text-[12.5px]" style={{ minWidth }}>
        <thead className="sticky top-0 z-10">
          <tr className="text-left bg-sky-600 text-white">
            <th className="p-2.5 w-10 bg-sky-600" />
            <th className="p-2.5 font-semibold w-[60px] bg-sky-600">S.No</th>
            <th className="p-2.5 font-semibold w-[80px] bg-sky-600">Job No</th>
            <th className="p-2.5 font-semibold w-[100px] bg-sky-600">Job Date</th>
            <th className="p-2.5 font-semibold w-[110px] bg-sky-600">SLA</th>
            <th className="p-2.5 font-semibold w-[110px] bg-sky-600">Exp. Delivery</th>
            <th className="p-2.5 font-semibold w-[140px] bg-sky-600">Status</th>
            {showJobMetrics && (
              <>
                <th className="p-2.5 font-semibold w-[100px] bg-sky-600">Job Hours</th>
                <th className="p-2.5 font-semibold w-[110px] bg-sky-600">Days Delay</th>
              </>
            )}
            <th className="p-2.5 font-semibold w-[140px] bg-sky-600">Service Head</th>
            <th className="p-2.5 font-semibold w-[120px] bg-sky-600">Job Category</th>
            <th className="p-2.5 font-semibold w-[140px] bg-sky-600">Job Type</th>
            <th className="p-2.5 font-semibold w-[120px] bg-sky-600">Supervisor</th>
            <th className="p-2.5 font-semibold w-[80px] bg-sky-600 text-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, idx) => {
            const sno = startSno + idx + 1;
            const isSel = selected.has(r.id);
            const baseTone = idx % 2 === 0 ? 'bg-white' : 'bg-sky-50/70';
            const metrics = showJobMetrics ? jobMetrics(r) : null;
            const negDelay = metrics && metrics.daysDelay && metrics.daysDelay.startsWith('-');
            return (
              <React.Fragment key={r.id}>
                {/* Primary row */}
                <tr className={`border-b border-sky-100 ${isSel ? 'bg-sky-100/80' : `${baseTone} hover:bg-sky-100/50`}`}>
                  <td className="p-2.5 align-top">
                    <input
                      type="checkbox"
                      checked={isSel}
                      onChange={() => togglePick(r.id)}
                      className="w-4 h-4 rounded border-slate-300 text-sky-600"
                      data-testid={`report-row-${r.id}`}
                    />
                  </td>
                  <td className="p-2.5 align-top text-slate-500">{sno}</td>
                  <td className="p-2.5 align-top font-bold text-green-900 text-[14.5px]" data-testid={`report-job-no-${r.id}`}>{r.job_no || '—'}</td>
                  <td className="p-2.5 align-top text-slate-500 whitespace-nowrap">{r.job_date || '—'}</td>
                  <td className="p-2.5 align-top whitespace-nowrap">
                    <SLABadge status={r.sla_status} daysLate={r.days_late} />
                  </td>
                  <td className="p-2.5 align-top text-slate-500 whitespace-nowrap">{r.expected_delivery_date || '—'}</td>
                  <td className="p-2.5 align-top"><StatusPill value={r.doc_type} /></td>
                  {showJobMetrics && (
                    <>
                      <td className="p-2.5 align-top whitespace-nowrap font-mono text-slate-700" data-testid={`report-job-hours-${r.id}`}>
                        {metrics?.jobHours || (
                          <span className="text-slate-300" title="Add Ramp In + Ramp Out times on Edit Job to compute Job Hours">—</span>
                        )}
                      </td>
                      <td className="p-2.5 align-top whitespace-nowrap font-mono" data-testid={`report-days-delay-${r.id}`}>
                        {metrics?.daysDelay
                          ? <span className={negDelay ? 'text-rose-600' : 'text-slate-700'}>{metrics.daysDelay}</span>
                          : <span className="text-slate-300" title="Add Vehicle In + Ramp In times on Edit Job to compute Days Delay">—</span>}
                      </td>
                    </>
                  )}
                  <td className="p-2.5 align-top text-slate-700">{r.service_head || '—'}</td>
                  <td className="p-2.5 align-top text-slate-700">{r.job_category || '—'}</td>
                  <td className="p-2.5 align-top text-slate-700">{r.job_type || '—'}</td>
                  <td className="p-2.5 align-top text-slate-700">{r.supervisor || '—'}</td>
                  <td className="p-2.5 align-top text-center">
                    <button
                      type="button"
                      onClick={() => onEdit && onEdit(r.id)}
                      data-testid={`edit-job-${r.id}`}
                      title="Edit Job Card"
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11.5px] font-medium text-sky-700 bg-sky-50 border border-sky-200 hover:bg-sky-100 hover:border-sky-300 transition-colors"
                    >
                      <Pencil size={12} /> Edit
                    </button>
                  </td>
                </tr>
                {/* Secondary row */}
                <tr className={`border-b border-sky-100 ${isSel ? 'bg-sky-100/80' : baseTone}`}>
                  <td colSpan={2} />
                  <td colSpan={showJobMetrics ? 4 : 2} className="px-2.5 pb-2.5 pt-0 align-top text-[11.5px]">
                    <div className="text-slate-400 mb-0.5">Reg / Party / Mobile</div>
                    <div className="leading-tight">
                      <span className="font-mono text-slate-700">{r.reg_no || '—'}</span><br/>
                      <span className="text-blue-900 font-bold text-[14.5px]" data-testid={`report-party-name-${r.id}`}>{r.party_name || '—'}</span><br/>
                      <span className="text-slate-700">{r.mobile_no_1 || '—'}</span>
                    </div>
                  </td>
                  <td className="px-2.5 pb-2.5 pt-0 align-top text-[11.5px]">
                    <div className="text-slate-400 mb-0.5">Vehicle In</div>
                    <div className="text-slate-700">
                      {r.vehicle_in_date || '—'}{r.vehicle_in_time ? ` ${r.vehicle_in_time}` : ''}
                    </div>
                  </td>
                  <td colSpan={2} className="px-2.5 pb-2.5 pt-0 align-top text-[11.5px]">
                    <div className="text-slate-400 mb-0.5">Customer's Voice</div>
                    <div className="text-slate-700 line-clamp-2 leading-snug" title={r.customer_voice || ''}>
                      {r.customer_voice || '—'}
                    </div>
                  </td>
                  <td className="px-2.5 pb-2.5 pt-0 align-top text-[11.5px]">
                    <div className="text-slate-400 mb-0.5">Chassis No</div>
                    <div className="font-mono text-slate-700 break-all">{r.chassis_no || '—'}</div>
                  </td>
                  <td className="px-2.5 pb-2.5 pt-0 align-top text-[11.5px]">
                    <div className="text-slate-400 mb-0.5">Engine No</div>
                    <div className="font-mono text-slate-700 break-all">{r.engine_no || '—'}</div>
                  </td>
                  <td className="px-2.5 pb-2.5 pt-0 align-top text-[11.5px]">
                    <div className="text-slate-400 mb-0.5">Model-Variant</div>
                    <div className="text-slate-700">{r.model_variant || '—'}</div>
                  </td>
                  <td className="px-2.5 pb-2.5 pt-0 align-top text-[11.5px]">
                    <div className="text-slate-400 mb-0.5">Technician</div>
                    <div className="text-slate-700">{r.technician || '—'}</div>
                  </td>
                  <td className="px-2.5 pb-2.5 pt-0 align-top" />
                </tr>
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Re-export StatusPill so existing imports of `StatusPill` from ReportTable
// (used by the Reconciliation sub-components) keep working with the new
// gradient palette.
export { StatusPill };
